import { useMemo, useState } from 'react';
import { Button, Box, InputLabel } from '@mui/material';
import { useTranslate, useRecordContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { GrayDateInputWithUpperLabel } from './GrayDateInputWithUpperLabel';

const textLinkSx = {
  p: 0,
  m: 0,
  minWidth: 0,
  minHeight: 0,
  textTransform: 'none',
  textDecoration: 'underline',
  justifyContent: 'flex-start',
  fontWeight: 'normal',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  '&:hover': {
    textDecoration: 'underline',
    backgroundColor: 'transparent',
  },
} as const;

type PlannedRenewalDateInputFieldProps = {
  label?: string;
  clearButtonPlacement?: 'inline' | 'below';
  fullWidth?: boolean;
};

export const PlannedRenewalDateInputField = (props: PlannedRenewalDateInputFieldProps) => {

  const {
    label = 'resources.security-inventory-items.fields.planned_inspection_date',
    clearButtonPlacement = 'inline',
    fullWidth = false,
  } = props;

  const translate = useTranslate();
  const record = useRecordContext();
  const { setValue } = useFormContext();

  const [addPlannedRenewalDateClicked, setAddPlannedRenewalDateClicked] = useState(false);
  const [removedPlannedRenewalDate, setRemovedPlannedRenewalDate] = useState(false);
  const showPlannedRenewalDateField = useMemo(() => {

    return (record && (record.plannedRenewalDate || addPlannedRenewalDateClicked)) && !removedPlannedRenewalDate;

  }, [record, addPlannedRenewalDateClicked, removedPlannedRenewalDate]);

  const clearPlannedRenewalDate = () => {

    setValue('plannedRenewalDate', null);
    setRemovedPlannedRenewalDate(true);

  };

  return (
    <Box sx={{
      position: clearButtonPlacement === 'inline' ? 'relative' : undefined,
      maxWidth: fullWidth ? undefined : 160,
      width: fullWidth ? '100%' : undefined,
      display: clearButtonPlacement === 'below' ? 'flex' : undefined,
      flexDirection: clearButtonPlacement === 'below' ? 'column' : undefined,
      alignItems: clearButtonPlacement === 'below' ? 'flex-start' : undefined,
    }}>
      {showPlannedRenewalDateField ? (
        <GrayDateInputWithUpperLabel
          source="plannedRenewalDate"
          label={label}
          helperText={false}
          size="medium"
          sx={{ width: fullWidth ? '100%' : 160, mt: 0, mb: 0 }}
        />
      ) : (
        <>
          <InputLabel className="input-label">
            {translate(label)}
          </InputLabel>
          <Box
            role="button"
            tabIndex={0}
            aria-label={translate('resources.security-inventory-items.text.not_planned_yet')}
            onClick={() => {

              setAddPlannedRenewalDateClicked(true);
              setRemovedPlannedRenewalDate(false);

            }}
            sx={{
              width: fullWidth ? '100%' : 160,
              height: 56,
              backgroundColor: '#f1f1f1',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              px: 1.5,
              color: 'text.secondary',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
          >
            {translate('resources.security-inventory-items.text.not_planned_yet')}
          </Box>
        </>
      )}
      {showPlannedRenewalDateField && (
        <Box sx={clearButtonPlacement === 'inline' ? {
          position: 'absolute',
          left: '100%',
          bottom: 0,
          ml: 1,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
        } : {
          mt: 0.5,
        }}>
          <Button sx={textLinkSx} onClick={clearPlannedRenewalDate}>
            {translate('general.text.clear')}
          </Button>
        </Box>
      )}
    </Box>
  );

};
